import React, { useMemo } from "react";
import { PatientMoodEntry } from "../../types/api";
import { MOOD_ORDER, MOOD_Y_POSITION, getMoodConfig } from "../../constants/mood";

interface MoodTrackingCardProps {
  entries: PatientMoodEntry[];
}

// Chart geometry (SVG user units; scaled responsively via viewBox).
const WIDTH = 960;
const HEIGHT = 285;
const PADDING = { top: 34, right: 18, bottom: 28, left: 18 };
const PLOT_W = WIDTH - PADDING.left - PADDING.right;
const PLOT_H = HEIGHT - PADDING.top - PADDING.bottom;

// Y domain spans the fixed mood positions (1 = lowest negative, 5 = highest positive).
const Y_MIN = 0;
const Y_MAX = 6;

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 30;

function yToPx(value: number): number {
  const t = (value - Y_MIN) / (Y_MAX - Y_MIN);
  return PADDING.top + (1 - t) * PLOT_H;
}

/**
 * Card 1 - Mood Tracking (30 days).
 * Renders each daily mood check-in at a fixed vertical position (positive moods
 * on top, Neutral in the middle, negative moods at the bottom) across a 30-day
 * X-axis so clinicians can spot deterioration, instability, consistency and
 * missing responses at a glance.
 */
export const MoodTrackingCard: React.FC<MoodTrackingCardProps> = ({ entries }) => {
  // Fixed 30-day window ending today.
  const { windowStart, windowEnd, dayTicks } = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    const start = new Date(end.getTime() - (WINDOW_DAYS - 1) * DAY_MS);
    start.setHours(0, 0, 0, 0);

    const ticks: Date[] = [];
    for (let i = 0; i < WINDOW_DAYS; i++) {
      ticks.push(new Date(start.getTime() + i * DAY_MS));
    }
    return { windowStart: start.getTime(), windowEnd: end.getTime(), dayTicks: ticks };
  }, []);

  const points = useMemo(() => {
    const span = windowEnd - windowStart || 1;
    return entries
      .map((entry) => {
        const config = getMoodConfig(entry.mood);
        const t = new Date(entry.created_at).getTime();
        if (!config || isNaN(t) || t < windowStart || t > windowEnd) return null;
        const xFrac = (t - windowStart) / span;
        return {
          x: PADDING.left + xFrac * PLOT_W,
          y: yToPx(MOOD_Y_POSITION[config.mood]),
          config,
        };
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);
  }, [entries, windowStart, windowEnd]);

  const formatTick = (d: Date) =>
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  // Label every few days to avoid crowding the axis.
  const tickStep = Math.ceil(WINDOW_DAYS / 12);

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-4" dir="rtl">
      {/* Card header: title + legend on one row */}
      <div className="flex items-center justify-between gap-4">
        {/* Card title */}
        <div className="shrink-0">
          <h3 className="text-lg font-bold text-gray-900">מעקב מצב רוח - 30 יום</h3>
          <span className="text-sm text-gray-500">מה שלומך היום?</span>
        </div>

        {/* Legend (reversed: negative -> neutral -> positive) */}
        <div className="flex flex-wrap justify-center gap-2 flex-1">
          {[...MOOD_ORDER].reverse().map((mood) => {
            const c = getMoodConfig(mood)!;
            return (
              <span
                key={mood}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium"
                style={{ borderColor: c.color, color: c.color, borderWidth: 4, borderStyle: "solid" }}
              >
                <span>{c.label}</span>
                <span className="text-2xl leading-none">{c.emoji}</span>
              </span>
            );
          })}
        </div>
      </div>

      {points.length === 0 ? (
        <div className="text-center text-xs text-gray-400 italic py-10">
          אין נתוני מצב רוח ל-30 הימים האחרונים
        </div>
      ) : (
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="w-full h-auto"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Horizontal gridlines */}
          {[1, 2, 3, 4, 5].map((v) => (
            <g key={v}>
              <line
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={yToPx(v)}
                y2={yToPx(v)}
                stroke="#9ca3af"
                strokeDasharray="4 4"
              />
              <text
                x={PADDING.left - 8}
                y={yToPx(v) + 5}
                textAnchor="end"
                fontSize="14"
                fill="#374151"
              >
                {v}
              </text>
            </g>
          ))}

          {/* X-axis date ticks */}
          {dayTicks.map((d, i) => {
            if (i % tickStep !== 0) return null;
            const span = windowEnd - windowStart || 1;
            const xFrac = (d.getTime() - windowStart) / span;
            const x = PADDING.left + xFrac * PLOT_W;
            return (
              <text
                key={i}
                x={x}
                y={HEIGHT - PADDING.bottom + 6}
                textAnchor="end"
                fontSize="12"
                fill="#374151"
                transform={`rotate(-45 ${x} ${HEIGHT - PADDING.bottom + 6})`}
              >
                {formatTick(d)}
              </text>
            );
          })}

          {/* Mood markers */}
          {points.map((p, i) => (
            <g key={i}>
              <text
                x={p.x}
                y={p.y - 22}
                textAnchor="middle"
                fontSize="13"
                fontWeight="600"
                fill={p.config.color}
              >
                {p.config.label}
              </text>
              <circle
                cx={p.x}
                cy={p.y}
                r="16"
                fill="#ffffff"
                stroke={p.config.color}
                strokeWidth="2.5"
              />
              <text x={p.x} y={p.y + 6} textAnchor="middle" fontSize="18">
                {p.config.emoji}
              </text>
            </g>
          ))}
        </svg>
      )}
    </div>
  );
};
